import axios from 'axios';

import type {
  AssetProcessingActivityDetail,
  AssetProcessingActivityReviewStatus,
  AssetProcessingActivitySaveInput,
  AssetProcessingActivitySavePurposeInput,
  CountryVolume,
  DpiaIndicationStatus,
  HandlingType,
  MinimumSecurityAssessment,
  OwnerAssetProcessingPurposeUpdateInput,
  OwnerCountryHandling,
  RetentionUnit,
} from 'config/api/assetProcessingActivities/assetProcessingActivities.types';
import type { AssetProcessingCountryRow } from 'config/api/assetRopa/assetRopa.types';
import type { ProcessingActivityDetail } from 'config/api/processingActivities/processingActivities.types';
import { getRopaCountriesForUi, getRopaCountryClassification } from 'config/dictionaries/ropaCountryMasterdata';

import { RETENTION_UNIT_OPTIONS, type AssetProcessingApaFormValues } from '../_shared/assetProcessingApa.types';
import type { CentralProcessingActivity } from '../_shared/ProcessingActivitySidePanel/ProcessingActivitySidePanel';
import { parseProcessingActivityDetailViewModel } from '../ProcessingActivityDetailPage/processingActivityDetail.parsers';
import { isLegitimateInterestsBasis } from '../ProcessingActivityEditorPage/processingActivityEditor.parsers';
import { getRetentionValueFieldError, isRetentionValueInvalid } from '../ProcessingActivityEditorPage/processingActivityEditor.validation';

export type AssetProcessingActivityEditorValidationErrors = Partial<Record<string, string>>;

export type DesiredCountryHandling = {
  countryCode: string;
  handlingType: HandlingType;
  volume: CountryVolume;
};

export type CountryHandlingDiff = {
  toCreate: DesiredCountryHandling[];
  toDeleteIds: number[];
};

const isBlank = (value: string) => !value.trim();

const HANDLING_FLAGS: Array<{
  flag: keyof Pick<AssetProcessingCountryRow, 'storage' | 'access' | 'support' | 'development'>;
  type: HandlingType;
}> = [
  { flag: 'storage', type: 'storage' },
  { flag: 'access', type: 'access' },
  { flag: 'support', type: 'support' },
  { flag: 'development', type: 'processing' },
];

const isVolume = (value: string): value is CountryVolume => ['low', 'medium', 'high', 'unknown'].includes(value);

const matchesDesiredHandling = (existing: OwnerCountryHandling, desired: DesiredCountryHandling) => {
  if (existing.countryCode !== desired.countryCode) return false;

  if (desired.handlingType === 'processing') {
    return existing.handlingType === 'processing' || existing.handlingType === 'subprocessor';
  }

  return existing.handlingType === desired.handlingType;
};

export const expandCountryRowsToHandlings = (rows: AssetProcessingCountryRow[]): DesiredCountryHandling[] => {
  const result: DesiredCountryHandling[] = [];

  rows.forEach(row => {
    const countryCode = row.countryCode.trim().toUpperCase();
    if (!countryCode) return;

    const volume: CountryVolume = isVolume(row.volume) ? row.volume : 'unknown';

    HANDLING_FLAGS.forEach(({ flag, type }) => {
      if (row[flag]) {
        result.push({ countryCode, handlingType: type, volume });
      }
    });
  });

  return result;
};

export const buildCountryHandlingDiff = (existing: OwnerCountryHandling[], desired: DesiredCountryHandling[]): CountryHandlingDiff => {
  const remaining = [...existing];
  const toCreate: DesiredCountryHandling[] = [];
  const toDeleteIds: number[] = [];

  desired.forEach(desiredItem => {
    const matchIndex = remaining.findIndex(item => matchesDesiredHandling(item, desiredItem));
    if (matchIndex === -1) {
      toCreate.push(desiredItem);
      return;
    }

    const [match] = remaining.splice(matchIndex, 1);
    if (match.volume !== desiredItem.volume) {
      toDeleteIds.push(match.id);
      toCreate.push(desiredItem);
    }
  });

  remaining.forEach(item => {
    toDeleteIds.push(item.id);
  });

  return { toCreate, toDeleteIds };
};

const isRetentionUnit = (value: string): value is Exclude<RetentionUnit, ''> =>
  (RETENTION_UNIT_OPTIONS as readonly string[]).includes(value);

const mapPurposeApplicationFields = (purpose: AssetProcessingApaFormValues['purposeApplications'][number]) => ({
  applicabilityStatus: purpose.applicabilityStatus,
  applicabilityJustification: purpose.justification,
  retentionTrigger: purpose.retentionTrigger,
  retentionValue: purpose.retentionValue,
  ...(isRetentionUnit(purpose.retentionUnit) ? { retentionUnit: purpose.retentionUnit } : {}),
  retentionComment: purpose.retentionComment,
  deletionOrAnonymisationRoutine: purpose.deletionRoutine,
});

export const mapPurposeApplicationToUpdateInput = (
  purpose: AssetProcessingApaFormValues['purposeApplications'][number],
  revision: number,
): OwnerAssetProcessingPurposeUpdateInput => ({
  revision,
  ...mapPurposeApplicationFields(purpose),
});

export const mapPurposeApplicationToSaveInput = (
  purpose: AssetProcessingApaFormValues['purposeApplications'][number],
): AssetProcessingActivitySavePurposeInput => ({
  id: purpose.id,
  ...mapPurposeApplicationFields(purpose),
});

export const mapFormValuesToSaveInput = (
  values: AssetProcessingApaFormValues,
  detail: Pick<AssetProcessingActivityDetail, 'revision' | 'updatedAt'>,
): AssetProcessingActivitySaveInput => ({
  revision: detail.revision,
  updatedAt: detail.updatedAt,
  dpiaIndicationStatus: values.dpiaIndicationStatus as DpiaIndicationStatus,
  dpiaIndicationComment: values.dpiaIndicationComment,
  minimumSecurityAssessment: values.minimumSecurityAssessment as MinimumSecurityAssessment,
  minimumSecurityAssessmentComment: values.minimumSecurityAssessmentComment,
  purposes: values.purposeApplications.map(mapPurposeApplicationToSaveInput),
  countries: expandCountryRowsToHandlings(values.countryRows),
});

export const validatePurposeJustificationsForSave = (
  values: AssetProcessingApaFormValues,
  messages: {
    purposeDoesNotApplyJustification: string;
    required: string;
  },
): AssetProcessingActivityEditorValidationErrors => {
  const errors: AssetProcessingActivityEditorValidationErrors = {};

  values.purposeApplications.forEach((purpose, index) => {
    if (purpose.applicabilityStatus === 'does_not_apply' && isBlank(purpose.justification)) {
      errors[`purpose.${index}.justification`] = messages.purposeDoesNotApplyJustification;
    }

    if (purpose.applicabilityStatus === 'applies' && !isRetentionUnit(purpose.retentionUnit)) {
      errors[`purpose.${index}.retentionUnit`] = messages.required;
    }
  });

  return errors;
};

export const validateAssetProcessingActivityForSubmit = (
  values: AssetProcessingApaFormValues,
  messages: {
    required: string;
    purposeNotAssessed: string;
    purposeAppliesIncomplete: string;
    purposeDoesNotApplyJustification: string;
    securityIncomplete: string;
    securityCommentRequired: string;
    countryRowInvalid: string;
    countriesRequired: string;
    dpiaCommentRequired: string;
    retentionValueNegative: string;
    retentionValueInteger: string;
    clarificationResponse?: string;
  },
  reviewStatus?: AssetProcessingActivityReviewStatus | null,
  clarificationResponse?: string,
): AssetProcessingActivityEditorValidationErrors => {
  const errors: AssetProcessingActivityEditorValidationErrors = {
    ...validatePurposeJustificationsForSave(values, messages),
  };

  if (values.minimumSecurityAssessment === 'not_assessed') {
    errors.minimumSecurityAssessment = messages.required;
  }

  if (['partially_meets', 'does_not_meet'].includes(values.minimumSecurityAssessment) && isBlank(values.minimumSecurityAssessmentComment)) {
    errors.minimumSecurityAssessmentComment = messages.securityCommentRequired;
  }

  values.purposeApplications.forEach((purpose, index) => {
    if (purpose.applicabilityStatus === 'not_assessed') {
      errors[`purpose.${index}.applicabilityStatus`] = messages.purposeNotAssessed;
    }

    if (purpose.applicabilityStatus === 'applies') {
      const missingFields =
        isBlank(purpose.justification) ||
        isBlank(purpose.retentionTrigger) ||
        purpose.retentionValue === null ||
        !isRetentionUnit(purpose.retentionUnit) ||
        isBlank(purpose.deletionRoutine);

      if (missingFields) {
        errors[`purpose.${index}.applies`] = messages.purposeAppliesIncomplete;
      }

      if (!isRetentionUnit(purpose.retentionUnit)) {
        errors[`purpose.${index}.retentionUnit`] = messages.required;
      }

      if (purpose.retentionValue != null && isRetentionValueInvalid(purpose.retentionValue)) {
        errors[`purpose.${index}.retentionValue`] = getRetentionValueFieldError(purpose.retentionValue, messages);
      }
    }
  });

  values.countryRows.forEach((row, index) => {
    const hasHandling = row.storage || row.access || row.support || row.development;
    if (!hasHandling || isBlank(row.volume) || isBlank(row.countryCode)) {
      errors[`country.${index}`] = messages.countryRowInvalid;
    }
  });

  if (expandCountryRowsToHandlings(values.countryRows).length === 0) {
    errors.countries = messages.countriesRequired;
  }

  if (values.dpiaIndicationStatus === 'indicated' && isBlank(values.dpiaIndicationComment)) {
    errors.dpiaIndicationComment = messages.dpiaCommentRequired;
  }

  if (reviewStatus === 'clarification_requested' && isBlank(clarificationResponse ?? '') && messages.clarificationResponse) {
    errors.clarificationResponse = messages.clarificationResponse;
  }

  return errors;
};

export const hasValidationErrors = (errors: AssetProcessingActivityEditorValidationErrors) => Object.keys(errors).length > 0;

const splitSecurityMeasures = (value: string): string[] =>
  value
    ? value
        .split(';')
        .map(item => item.trim())
        .filter(Boolean)
    : [];

/** Legacy UI adapter for side panel / purpose section until those components are schema-aligned. */
export const mapProcessingActivityToSidePanel = (
  detail: AssetProcessingActivityDetail,
  publishedActivity?: ProcessingActivityDetail | null,
): CentralProcessingActivity => {
  const publishedViewModel = publishedActivity ? parseProcessingActivityDetailViewModel(publishedActivity) : null;
  const categoryNames = (categories: Array<{ name: string }> | undefined): string[] => categories?.map(category => category.name) ?? [];

  return {
    name: detail.processingActivity.name,
    description: publishedActivity?.description || detail.processingActivity.description,
    processingRole: publishedActivity?.processingRole ?? '',
    dataSubjectCategories: categoryNames(publishedViewModel?.categories.dataSubjectCategories),
    personalDataCategories: categoryNames(publishedViewModel?.categories.personalDataCategories),
    recipientCategories: categoryNames(publishedViewModel?.categories.recipientCategories),
    minimumSecurityMeasures: splitSecurityMeasures(
      publishedActivity?.minimumSecurityMeasures || detail.processingActivity.minimumSecurityMeasures,
    ),
    purposes: detail.processingActivity.purposes.map(purpose => ({
      id: purpose.id,
      name: purpose.purposeText,
      legalBasis: purpose.legalBasis,
      legalBasisComment: purpose.legalBasisComment,
      liaRequired: isLegitimateInterestsBasis(purpose.legalBasis),
      retentionTrigger: purpose.retentionTrigger,
      retentionValue: purpose.retentionValue,
      retentionUnit: purpose.retentionUnit,
      retentionComment: purpose.retentionComment,
    })),
  };
};

/** Maps schema-aligned APA detail into the legacy editor form shape used by current UI sections. */
export const mapDetailToFormValues = (detail: AssetProcessingActivityDetail): AssetProcessingApaFormValues => {
  const purposeNameById = new Map(detail.processingActivity.purposes.map(purpose => [purpose.id, purpose.purposeText]));
  const countryNameByCode = new Map(getRopaCountriesForUi().map(country => [country.iso2, country.name]));

  const countryRowsByCode = new Map<string, AssetProcessingApaFormValues['countryRows'][number]>();

  detail.countries.forEach(country => {
    const existing = countryRowsByCode.get(country.countryCode) ?? {
      id: country.id,
      countryCode: country.countryCode,
      countryName: countryNameByCode.get(country.countryCode) ?? country.countryCode,
      classification: getRopaCountryClassification(country.countryCode),
      storage: false,
      access: false,
      support: false,
      development: false,
      volume: country.volume,
    };

    if (country.handlingType === 'storage') existing.storage = true;
    if (country.handlingType === 'access') existing.access = true;
    if (country.handlingType === 'support') existing.support = true;
    if (country.handlingType === 'processing' || country.handlingType === 'subprocessor') existing.development = true;
    existing.volume = country.volume;
    countryRowsByCode.set(country.countryCode, existing);
  });

  return {
    securityMeasureCategories: [],
    securityComment: detail.processingActivity.minimumSecurityMeasures || '',
    minimumSecurityAssessment: detail.minimumSecurityAssessment,
    minimumSecurityAssessmentComment: detail.minimumSecurityAssessmentComment,
    dpiaIndicationStatus: detail.dpiaIndicationStatus,
    dpiaIndicationComment: detail.dpiaIndicationComment,
    purposeApplications: detail.purposes.map(purpose => ({
      id: purpose.id,
      processingPurposeId: purpose.sourceProcessingPurposeId,
      name: purposeNameById.get(purpose.sourceProcessingPurposeId) ?? '',
      applicabilityStatus: purpose.applicabilityStatus,
      applicationDescription: '',
      justification: purpose.applicabilityJustification,
      retentionTrigger: purpose.retentionTrigger,
      retentionValue: purpose.retentionValue,
      retentionUnit: purpose.retentionUnit,
      retentionComment: purpose.retentionComment,
      deletionRoutine: purpose.deletionOrAnonymisationRoutine,
    })),
    countryRows: Array.from(countryRowsByCode.values()),
  };
};

const APA_REVISION_CONFLICT_CODE = 'asset_processing_activity_revision_conflict';
const INACTIVE_REVIEW_ROUND_CODES = ['inactive_review_round', 'no_active_owner_review'];

const readAxiosErrorCode = (error: unknown): string | undefined => {
  if (!axios.isAxiosError(error) || !error.response?.data || typeof error.response.data !== 'object') {
    return undefined;
  }

  const { code } = error.response.data as { code?: unknown };
  return typeof code === 'string' ? code : undefined;
};

export const isAssetProcessingActivityUnavailableError = (error: unknown): boolean => {
  if (!axios.isAxiosError(error)) {
    return false;
  }

  const status = error.response?.status;
  return status === 403 || status === 404;
};

export const isAssetProcessingActivityRevisionConflict = (error: unknown): boolean => {
  if (!axios.isAxiosError(error) || error.response?.status !== 409) {
    return false;
  }

  return error.response.status === 409 && readAxiosErrorCode(error) === APA_REVISION_CONFLICT_CODE;
};

export const isAssetProcessingActivityInactiveReviewRound = (error: unknown): boolean => {
  if (!axios.isAxiosError(error) || error.response?.status !== 409) {
    return false;
  }

  const code = readAxiosErrorCode(error);
  return Boolean(code && INACTIVE_REVIEW_ROUND_CODES.includes(code));
};

export const isAssetProcessingActivityRevisionValidationError = (error: unknown): boolean => {
  if (!axios.isAxiosError(error) || error.response?.status !== 400) {
    return false;
  }

  const { data } = error.response;
  return Boolean(data && typeof data === 'object' && 'revision' in data);
};

const firstValidationMessage = (value: unknown): string | undefined => {
  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed.length > 0 ? trimmed : undefined;
  }

  if (Array.isArray(value)) {
    return value.map(firstValidationMessage).find((message): message is string => Boolean(message));
  }

  if (value && typeof value === 'object') {
    return Object.values(value as Record<string, unknown>)
      .map(firstValidationMessage)
      .find((message): message is string => Boolean(message));
  }

  return undefined;
};

const PURPOSE_SAVE_FIELD_TO_FORM: Record<string, string> = {
  applicability_status: 'applicabilityStatus',
  applicability_justification: 'justification',
  retention_unit: 'retentionUnit',
  retention_value: 'retentionValue',
};

const CORE_SAVE_FIELD_TO_FORM: Record<string, string> = {
  dpia_indication_status: 'dpiaIndicationStatus',
  dpia_indication_comment: 'dpiaIndicationComment',
  minimum_security_assessment: 'minimumSecurityAssessment',
  minimum_security_assessment_comment: 'minimumSecurityAssessmentComment',
  clarification_response: 'clarificationResponse',
};

export const mapAssetProcessingActivitySaveValidationErrors = (
  data: unknown,
  purposeIndex?: number,
): AssetProcessingActivityEditorValidationErrors => {
  if (!data || typeof data !== 'object') {
    return {};
  }

  const payload = data as Record<string, unknown>;
  const errors: AssetProcessingActivityEditorValidationErrors = {};

  if (Array.isArray(payload.purposes)) {
    payload.purposes.forEach((purpose, index) => {
      if (!purpose || typeof purpose !== 'object' || Array.isArray(purpose)) {
        const message = firstValidationMessage(purpose);
        if (message) {
          errors[`purpose.${index}.applies`] = message;
        }
        return;
      }

      Object.entries(purpose as Record<string, unknown>).forEach(([key, value]) => {
        const message = firstValidationMessage(value);
        if (!message) {
          return;
        }

        const formField = PURPOSE_SAVE_FIELD_TO_FORM[key];
        errors[formField ? `purpose.${index}.${formField}` : `purpose.${index}.applies`] = message;
      });
    });
  } else {
    const retentionMessage = firstValidationMessage(payload.retention_value);
    if (retentionMessage) {
      errors[`purpose.${purposeIndex ?? 0}.retentionValue`] = retentionMessage;
    }
  }

  if (Array.isArray(payload.countries)) {
    payload.countries.forEach((country, index) => {
      const message = firstValidationMessage(country);
      if (message) {
        errors[`country.${index}`] = message;
      }
    });
  } else {
    const countriesMessage = firstValidationMessage(payload.countries);
    if (countriesMessage) {
      errors.countries = countriesMessage;
    }
  }

  Object.entries(CORE_SAVE_FIELD_TO_FORM).forEach(([dtoKey, formKey]) => {
    const message = firstValidationMessage(payload[dtoKey]);
    if (message) {
      errors[formKey] = message;
    }
  });

  return errors;
};

export const getAssetProcessingActivitySaveValidationErrors = (error: unknown): AssetProcessingActivityEditorValidationErrors => {
  if (!axios.isAxiosError(error) || error.response?.status !== 400 || isAssetProcessingActivityRevisionValidationError(error)) {
    return {};
  }

  return mapAssetProcessingActivitySaveValidationErrors(error.response?.data);
};
